import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    makeStyles,
    MenuItem,
    TextField,
    Typography,
} from "@material-ui/core";
import RepeatIcon    from "@material-ui/icons/Repeat";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EventIcon     from "@material-ui/icons/Event";
import { useSnackbar } from "notistack";
import { Post } from "../../../../actions";
import {
    // TODO: Add to apiUrls constants
    // Create_BlockRule_URL → POST /api/block-services
    // Update_BlockRule_URL → POST /api/block-services/update
    Create_BlockRule_URL,
    Update_BlockRule_URL,
} from "../../../../constants/apiUrls";
import { getTranslation } from "../../../../heplers/translationHelper";

// ─── constants ───────────────────────────────────────────────────────────────
const BLOCK_TYPES = [
    {
        value: "recurring",
        label: "Recurring",
        description: "Repeats every week on selected days",
        icon: RepeatIcon,
        color: "#1565c0",
        bg: "#e3f2fd",
    },
    {
        value: "period",
        label: "By Period",
        description: "Blocked between two dates",
        icon: DateRangeIcon,
        color: "#e65100",
        bg: "#fff3e0",
    },
    {
        value: "date",
        label: "Specific Date",
        description: "Blocked on one specific date",
        icon: EventIcon,
        color: "#880e4f",
        bg: "#fce4ec",
    },
];

const DAY_OPTIONS = [
    { id: "0", label: "Sun" },
    { id: "1", label: "Mon" },
    { id: "2", label: "Tue" },
    { id: "3", label: "Wed" },
    { id: "4", label: "Thu" },
    { id: "5", label: "Fri" },
    { id: "6", label: "Sat" },
    
];

const DAY_TIME_OPTIONS = [
    { value: 0, label: "Morning"   },
    { value: 1, label: "Evening"   },
    { value: 2, label: "Whole Day" },
];

const today = () => new Date().toISOString().split("T")[0];

const defaultForm = {
    blockType:    "recurring",
    days:         [],
    fromDate:     today(),
    toDate:       today(),
    specificDate: today(),
    dayTime:      2,
    rooms:      [],
    reason:       "",
};

// ─── styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        backgroundColor: "#0d4f6c",
        color: "#fff",
        padding: "14px 24px",
        "& .MuiTypography-root": { color: "#fff", fontWeight: 600, fontSize: 16 },
    },
    content: {
        paddingTop: "20px !important",
        paddingBottom: 8,
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: 700,
        color: "#78909c",
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 8,
        marginTop: 4,
    },
    typeGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 8,
        marginBottom: 20,
    },
    typeCard: {
        border: "2px solid #eceff1",
        borderRadius: 8,
        padding: "10px 12px",
        cursor: "pointer",
        transition: "all 0.15s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        "&:hover": { borderColor: "#b0bec5" },
    },
    typeCardActive: {
        borderWidth: 2,
    },
    typeIcon: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 2,
    },
    typeLabel: {
        fontSize: 12,
        fontWeight: 700,
        textAlign: "center",
    },
    typeDesc: {
        fontSize: 10,
        textAlign: "center",
        color: "#90a4ae",
        lineHeight: 1.3,
    },
    dayChips: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 16,
    },
    dayChip: {
        cursor: "pointer",
        fontWeight: 600,
        fontSize: 12,
        transition: "all 0.15s",
    },
    dateRow: {
        display: "flex",
        gap: 12,
        marginBottom: 16,
    },
    field: {
        marginBottom: 16,
    },
    roomGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 16,
    },
    roomChip: {
        cursor: "pointer",
        fontWeight: 500,
        fontSize: 12,
        transition: "all 0.15s",
        border: "1.5px solid #eceff1",
    },
    roomChipSelected: {
        backgroundColor: "#0d4f6c",
        color: "#fff",
        border: "1.5px solid #0d4f6c",
        "&:hover": { backgroundColor: "#0a3d55" },
    },
    errorText: {
        color: "#f44336",
        fontSize: 11,
        marginTop: -10,
        marginBottom: 8,
    },
    divider: {
        height: 1,
        backgroundColor: "#eceff1",
        margin: "12px 0",
    },
    saveBtn: {
        backgroundColor: "#0d4f6c",
        color: "#fff",
        fontWeight: 600,
        "&:hover": { backgroundColor: "#0a3d55" },
        "&:disabled": { backgroundColor: "#b0bec5" },
    },
}));

// ─── component ───────────────────────────────────────────────────────────────
const AddBlockRuleModal = ({ open, onClose, rooms, editingRule, onSaved }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [form, setForm]         = useState(defaultForm);
    const [errors, setErrors]     = useState({});
    const [saving, setSaving]     = useState(false);

    // populate form when editing
    useEffect(() => {
        if (open) {
            if (editingRule) {
                setForm({
                    blockType:    editingRule.blockType    || "recurring",
                    days:         editingRule.days         || [],
                    fromDate:     editingRule.fromDate     || today(),
                    toDate:       editingRule.toDate       || today(),
                    specificDate: editingRule.specificDate || today(),
                    dayTime:      editingRule.dayTime      ?? 2,
                    rooms:      editingRule.rooms      || [],
                    reason:       editingRule.reason       || "",
                });
            } else {
                setForm(defaultForm);
            }
            setErrors({});
        }
    }, [open, editingRule]);

    // ── field helpers ────────────────────────────────────────────────────────
    const set = (field, value) => {
        setForm((f) => ({ ...f, [field]: value }));
        setErrors((e) => ({ ...e, [field]: "" }));
    };

    const toggleDay = (dayId) => {
        const days = form.days.includes(dayId)
            ? form.days.filter((d) => d !== dayId)
            : [...form.days, dayId];
        set("days", days);
    };

    const toggleRoom = (roomId) => {
        const rooms = form.rooms.includes(roomId)
            ? form.rooms.filter((r) => r !== roomId)
            : [...form.rooms, roomId];
        set("rooms", rooms);
    };

    // ── validation ───────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (form.blockType === "recurring" && form.days.length === 0)
            e.days = getTranslation("Select at least one day", "Select at least one day", "Select at least one day");
        if (form.blockType === "period") {
            if (!form.fromDate) e.fromDate = getTranslation("Required", "Required", "Required");
            if (!form.toDate)   e.toDate   = getTranslation("Required", "Required", "Required");
            if (form.fromDate && form.toDate && form.toDate < form.fromDate)
                e.toDate = getTranslation("Must be after start date", "Must be after start date", "Must be after start date");
        }
        if (form.blockType === "date" && !form.specificDate)
            e.specificDate = getTranslation("Required", "Required", "Required");
        if (form.rooms.length === 0)
            e.rooms = getTranslation("Select at least one room", "Select at least one room", "Select at least one room");
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ── save ─────────────────────────────────────────────────────────────────
    const handleSave = () => {
        if (!validate()) return;
        setSaving(true);

const payload = {
    ...(editingRule?.id ? { id: editingRule.id } : {}),
    blockType:  form.blockType,
    dayTime:    form.dayTime,                                          // int: 0=Morning,1=Afternoon,2=Evening,3=WholeDay
    rooms:      form.rooms.map((id) => rooms.find((r) => r.id === id)?.name || ""),  // string[] — room names
    reason:     form.reason || "",
 
    // recurring
    days:       form.blockType === "recurring" ? form.days : [],       // List<int>
 
    // period
    fromDate:   form.blockType === "period" ? form.fromDate : null,    // "yyyy-MM-dd"
    toDate:     form.blockType === "period" ? form.toDate   : null,    // "yyyy-MM-dd"
 
    // specific date
    date:       form.blockType === "date"   ? form.specificDate : null, // "yyyy-MM-dd"
};
        const url = editingRule?.id ? Update_BlockRule_URL : Create_BlockRule_URL;

        Post(
    payload,
    url,
    null,
    (res) => {
        enqueueSnackbar(
            getTranslation("Block rule saved", "Block rule saved", "Block rule saved"),
            { variant: "success" }
        );
        setSaving(false);
        onSaved();
        onClose();
    },
    (err) => {
        enqueueSnackbar(
            err?.data || getTranslation("Something went wrong", "Something went wrong", "Something went wrong"),
            { variant: "error" }
        );
        setSaving(false);
    }
);

        /**
         * TODO — API:
         *   Create: POST /api/block-services          body: payload (without id)
         *   Update: POST /api/block-services/update   body: payload (with id)
         *
         * const url = editingRule?.id ? Update_BlockRule_URL : Create_BlockRule_URL;
         * Post(payload, url, null,
         *   (res) => {
         *     enqueueSnackbar("Saved successfully", { variant: "success" });
         *     setSaving(false);
         *     onSaved();
         *     onClose();
         *   },
         *   (err) => {
         *     enqueueSnackbar(err?.data || "Something went wrong", { variant: "error" });
         *     setSaving(false);
         *   }
         * );
         *
         * Remove mock below:
         */
        // setTimeout(() => {
        //     enqueueSnackbar(
        //         getTranslation("Block rule saved", "Block rule saved", "Block rule saved"),
        //         { variant: "success" }
        //     );
        //     setSaving(false);
        //     onSaved();
        //     onClose();
        // }, 600);
    };

    // ── render ───────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle className={classes.dialogTitle}>
                {editingRule
                    ? getTranslation("Edit Block Rule", "Edit Block Rule", "Edit Block Rule")
                    : getTranslation("Add Block Rule", "Add Block Rule", "Add Block Rule")}
            </DialogTitle>

            <DialogContent className={classes.content}>

                {/* ── Block Type selector ─────────────────────────────────── */}
                <Typography className={classes.sectionLabel}>
                    {getTranslation("Block Type", "Block Type", "Block Type")}
                </Typography>
                <Box className={classes.typeGrid}>
                    {BLOCK_TYPES.map((type) => {
                        const Icon     = type.icon;
                        const isActive = form.blockType === type.value;
                        return (
                            <Box
                                key={type.value}
                                className={classes.typeCard}
                                onClick={() => set("blockType", type.value)}
                                style={{
                                    borderColor:     isActive ? type.color : "#eceff1",
                                    backgroundColor: isActive ? type.bg    : "#fff",
                                }}
                            >
                                <Box
                                    className={classes.typeIcon}
                                    style={{ backgroundColor: isActive ? type.color : "#eceff1" }}
                                >
                                    <Icon style={{ fontSize: 16, color: isActive ? "#fff" : "#90a4ae" }} />
                                </Box>
                                <Typography
                                    className={classes.typeLabel}
                                    style={{ color: isActive ? type.color : "#37474f" }}
                                >
                                    {getTranslation(type.label, type.label, type.label)}
                                </Typography>
                                <Typography className={classes.typeDesc}>
                                    {getTranslation(type.description, type.description, type.description)}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Box className={classes.divider} />

                {/* ── When ────────────────────────────────────────────────── */}
                <Typography className={classes.sectionLabel} style={{ marginTop: 12 }}>
                    {getTranslation("When", "When", "When")}
                </Typography>

                {/* Recurring — day chips */}
                {form.blockType === "recurring" && (
                    <>
                        <Box className={classes.dayChips}>
                            {DAY_OPTIONS.map((day) => {
                                const selected = form.days.includes(day.id);
                                return (
                                    <Chip
                                        key={day.id}
                                        label={getTranslation(day.label, day.label, day.label)}
                                        className={classes.dayChip}
                                        onClick={() => toggleDay(day.id)}
                                        style={{
                                            backgroundColor: selected ? "#6a1b9a" : "#f3e5f5",
                                            color:           selected ? "#fff"    : "#6a1b9a",
                                            borderColor:     selected ? "#6a1b9a" : "transparent",
                                        }}
                                    />
                                );
                            })}
                        </Box>
                        {errors.days && (
                            <Typography className={classes.errorText}>{errors.days}</Typography>
                        )}
                    </>
                )}

                {/* Period — date range */}
                {form.blockType === "period" && (
                    <Box className={classes.dateRow}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="date"
                            label={getTranslation("From", "From", "From")}
                            value={form.fromDate}
                            onChange={(e) => set("fromDate", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.fromDate}
                            helperText={errors.fromDate}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="date"
                            label={getTranslation("To", "To", "To")}
                            value={form.toDate}
                            onChange={(e) => set("toDate", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.toDate}
                            helperText={errors.toDate}
                        />
                    </Box>
                )}

                {/* Specific date */}
                {form.blockType === "date" && (
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        type="date"
                        label={getTranslation("Date", "Date", "Date")}
                        value={form.specificDate}
                        onChange={(e) => set("specificDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.specificDate}
                        helperText={errors.specificDate}
                        className={classes.field}
                    />
                )}

                {/* ── Day Time ─────────────────────────────────────────────── */}
                <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={getTranslation("Day Time", "Day Time", "Day Time")}
                    value={form.dayTime}
                    onChange={(e) => set("dayTime", e.target.value)}
                    className={classes.field}
                >
                    {DAY_TIME_OPTIONS.map((dt) => (
                        <MenuItem key={dt.value} value={dt.value}>
                            {getTranslation(dt.label, dt.label, dt.label)}
                        </MenuItem>
                    ))}
                </TextField>

                <Box className={classes.divider} />

                {/* ── Rooms ───────────────────────────────────────────────── */}
                <Typography className={classes.sectionLabel} style={{ marginTop: 12 }}>
                    {getTranslation("Rooms", "Rooms", "Rooms")}
                </Typography>
                <Box className={classes.roomGrid}>
                    {rooms.map((room) => {
                        const selected = form.rooms.includes(room.id);
                        return (
                            <Chip
                                key={room.id}
                                label={`${room.title} (${room.totalSeats})`}
                                className={`${classes.roomChip} ${selected ? classes.roomChipSelected : ""}`}
                                onClick={() => toggleRoom(room.id)}
                                style={!selected ? { backgroundColor: "#f5f9fb", color: "#0d4f6c" } : {}}
                            />
                        );
                    })}
                </Box>
                {errors.rooms && (
                    <Typography className={classes.errorText}>{errors.rooms}</Typography>
                )}

                <Box className={classes.divider} />

                {/* ── Reason ──────────────────────────────────────────────── */}
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={getTranslation("Reason (optional)", "Reason (optional)", "Reason (optional)")}
                    value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                    placeholder={getTranslation(
                        "e.g. Renovation, private event, staff training...",
                        "e.g. Renovation, private event, staff training...",
                        "e.g. Renovation, private event, staff training..."
                    )}
                    style={{ marginTop: 12 }}
                />
            </DialogContent>

            <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #eceff1" }}>
                <Button onClick={onClose} disabled={saving}>
                    {getTranslation("Cancel", "Cancel", "Cancel")}
                </Button>
                <Button
                    variant="contained"
                    className={classes.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                >
                    {getTranslation("Save Block Rule", "Save Block Rule", "Save Block Rule")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddBlockRuleModal;